import { useContext } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { format, sub } from "date-fns";
import { UserContext } from "context/user";
import apiRequest from "api/apiRequest";

const generateQuery = (data) => {
  const { selectedFields, selectedLayer, searchDays, farmId } = data;

  if (selectedLayer === "zoning") {
    let endpointUrl;
    const fieldsQuery = selectedFields
      .map((field) => `fields=${field}`)
      .join("&");
    endpointUrl = `v2/zoning/orders?farm_id=${farmId}&status=paid`;

    if (selectedFields.length) {
      endpointUrl += `&${fieldsQuery}`;
    }

    return endpointUrl;
  }

  if (selectedLayer === "stack") {
    let endpointUrl;
    const fieldsQuery = selectedFields
      .map((field) => `fields=${field}`)
      .join("&");
    endpointUrl = `v2/stack/orders?farm_id=${farmId}&status=completed`;

    if (selectedFields.length) {
      endpointUrl += `&${fieldsQuery}`;
    }

    return endpointUrl;
  }

  const fieldsQuery = selectedFields
    .map((field) => `field_ids=${field}`)
    .join("&");
  const layerQuery = `&layer_filter=${selectedLayer}`;
  const searchDaysQuery =
    selectedLayer === "high_res" ? "" : `&search_days=${searchDays}`;

  return `v2/catalog?${fieldsQuery}${layerQuery}${searchDaysQuery}`;
};

const fetchCatalog = async (query, token) => {
  const response = await apiRequest.get(query, {
    headers: {
      authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const useMappingCatalogQuery = (data) => {
  const { token } = useContext(UserContext);
  const query = generateQuery(data);
  const { selectedLayer } = data;
  const currentDate = format(new Date(), "yyyy-MM-dd");

  return useInfiniteQuery(
    ["catalog", query, token],
    ({ pageParam = currentDate }) =>
      fetchCatalog(`${query}&latest_date_utc=${pageParam}`, token),
    {
      enabled: false,
      retry: false,
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        const { dates } = lastPage.features[0].properties;
        const sortedDates = dates.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );

        if (sortedDates.length === 0) return undefined;

        if (selectedLayer === "sensor_em") {
          return sortedDates;
        }
        return format(
          sub(new Date(sortedDates[sortedDates.length - 1].date), { days: 1 }),
          "yyyy-MM-dd"
        );
      },
    }
  );
};

export const useZoningCatalogQuery = (data) => {
  const { token } = useContext(UserContext);
  const query = generateQuery(data);

  return useQuery(
    ["zoning-catalog", query, token],
    () => fetchCatalog(query, token),
    { enabled: false, retry: false, cacheTime: 0 }
  );
};

export const useStackCatalogQuery = (data) => {
  const { token } = useContext(UserContext);
  const query = generateQuery(data);

  return useQuery(
    ["stack-catalog", query, token],
    () => fetchCatalog(query, token),
    { enabled: false, retry: false, cacheTime: 0 }
  );
};
